// ============================================================
//  TabManager Component — UI for Managing Dynamic Tabs
// ============================================================

import React, { useState, useRef, useEffect } from 'react';
import { DynamicTab } from '../types/tabs';
import { useTabContext } from '../providers/TabProvider';
import './TabManager.css';

interface TabManagerProps {
  onTabChange?: (tabId: string) => void;
  onClose?: () => void;
}

export function TabManager({
  onTabChange,
  onClose,
}: TabManagerProps): React.ReactElement {
  const {
    tabs,
    activeTab,
    editing,
    draggedTab,
    addTab,
    updateTab,
    updateTabNoSave,
    deleteTab,
    reorderTabs,
    setActiveTab,
    setEditingTab,
    setDraggedTab,
    canDeleteTab,
    canAddTab,
    triggerSave,
    getTabs,
  } = useTabContext();

  const [newTabLabel, setNewTabLabel] = useState('');
  const [showIconPicker, setShowIconPicker] = useState<string | null>(null);
  const draggedOverIndex = useRef<number | null>(null);
  const [previousActiveTab, setPreviousActiveTab] = useState<string | null>(
    activeTab
  );

  // Form state for editing
  const [editForm, setEditForm] = useState<{
    label: string;
    slug: string;
  } | null>(null);

  // Track if form has changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Handle when activeTab changes (especially when new tabs are added)
  React.useEffect(() => {
    if (activeTab && activeTab !== previousActiveTab) {
      onTabChange?.(activeTab);
      setPreviousActiveTab(activeTab);
    }
  }, [activeTab, previousActiveTab, onTabChange]);

  // Handle starting edit mode
  const handleStartEdit = (tab: DynamicTab): void => {
    const formData = {
      label: tab.label,
      slug: tab.slug,
    };
    setEditForm(formData);
    setHasUnsavedChanges(false);
    setEditingTab(tab.id);
  };

  // Handle toggle active status
  const handleToggleActive = (tab: DynamicTab): void => {
    // Current state: treat undefined as active (true), preserve explicit false
    const currentlyActive = tab.isActive ?? true;
    const newActiveState = !currentlyActive;
    // Update tab without automatic save
    updateTabNoSave(tab.id, {
      isActive: newActiveState,
    });

    setTimeout(() => {
      triggerSave();
    }, 1000);
  };

  // Handle form field changes
  const handleFormChange = (field: string, value: string | boolean): void => {
    if (!editForm) return;

    const updates = { ...editForm, [field]: value };

    // Auto-generate slug when label changes
    if (field === 'label' && typeof value === 'string') {
      updates.slug = value
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
    }

    setEditForm(updates);
    setHasUnsavedChanges(true);
  };

  // Handle saving changes
  const handleSaveEdit = (tabId: string): void => {
    if (!editForm) return;

    // Get current tab to preserve existing values
    const currentTab = tabs.find((t: any) => t.id === tabId);

    // Update tab without automatic save - preserve isDefault and isActive
    updateTabNoSave(tabId, {
      label: editForm.label,
      slug: editForm.slug,
      // Preserve existing isDefault and isActive values exactly as they are
      isDefault: currentTab?.isDefault ?? false,
      isActive: currentTab?.isActive, // Preserve exact value (don't default)
    });

    // Manually trigger save to API
    setTimeout(() => {
      triggerSave();
    }, 1000);

    setEditForm(null);
    setEditingTab(null);
    setHasUnsavedChanges(false);
  };

  // Handle canceling edit
  const handleCancelEdit = (): void => {
    setEditForm(null);
    setEditingTab(null);
    setHasUnsavedChanges(false);
  };

  // Handle drag and drop
  const handleDragStart = (
    e: React.DragEvent,
    tabId: string,
    index: number
  ): void => {
    setDraggedTab(tabId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', tabId);
  };

  const handleDragOver = (e: React.DragEvent, index: number): void => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    draggedOverIndex.current = index;
  };

  const handleDrop = (e: React.DragEvent, toIndex: number): void => {
    e.preventDefault();
    if (draggedTab) {
      const fromIndex = tabs.findIndex((tab: any) => tab.id === draggedTab);
      if (fromIndex !== -1 && fromIndex !== toIndex) {
        reorderTabs(fromIndex, toIndex);
      }
    }
    setDraggedTab(null);
    draggedOverIndex.current = null;
  };

  const handleAddTab = (): void => {
    if (newTabLabel.trim() && canAddTab()) {
      addTab(newTabLabel.trim());
      setNewTabLabel('');
    }
  };

  const handleTabSelect = (tabId: string): void => {
    setActiveTab(tabId);
    onTabChange?.(tabId);
  };

  return (
    <div className="tab-manager">
      <div className="tab-manager-header">
        <h3>Gestion des Pages</h3>
        <button className="btn-close" onClick={onClose} title="Fermer">
          ✕
        </button>
      </div>

      <div className="tab-manager-content">
        {/* Current Tabs */}
        <div className="tabs-list">
          <div className="tabs-list-header">
            <h4>Pages ({tabs.length})</h4>
            <small>Glissez pour réorganiser</small>
          </div>

          {tabs.map((tab: any, index: number) => (
            <div
              key={tab.id}
              className={`tab-item ${tab.id === activeTab ? 'active' : ''} ${
                tab.id === draggedTab ? 'dragging' : ''
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, tab.id, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={() => setDraggedTab(null)}
            >
              <div className="drag-handle" title="Glisser pour réorganiser">
                ⋮⋮
              </div>

              {editing === tab.id ? (
                <div className="tab-edit-form">
                  <div className="edit-form-header">
                    <h4>✏️ Modifier la page</h4>
                  </div>

                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>Nom de la page</label>
                      <input
                        type="text"
                        value={editForm?.label ?? ''}
                        onChange={(e) =>
                          handleFormChange('label', e.target.value)
                        }
                        className="tab-input"
                        placeholder="Nom de la page"
                      />
                    </div>

                    <div className="edit-field">
                      <label>Slug URL</label>
                      <div className="slug-input-wrapper">
                        <span className="slug-prefix">/</span>
                        <input
                          type="text"
                          value={editForm?.slug ?? ''}
                          onChange={(e) =>
                            handleFormChange('slug', e.target.value)
                          }
                          className="tab-input slug-input"
                          placeholder="slug-url"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="edit-actions">
                    <button
                      className={`btn-save ${
                        hasUnsavedChanges ? 'has-changes' : ''
                      }`}
                      onClick={() => handleSaveEdit(tab.id)}
                      disabled={!editForm?.label?.trim()}
                    >
                      ✓{' '}
                      {hasUnsavedChanges
                        ? 'Enregistrer les modifications'
                        : 'Enregistrer'}
                    </button>
                    <button className="btn-cancel" onClick={handleCancelEdit}>
                      ✕ Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div className="tab-info">
                  <button
                    className="tab-selector"
                    onClick={() => handleTabSelect(tab.id)}
                  >
                    <span className="tab-label">{tab.label}</span>
                    <span className="tab-slug">/{tab.slug}</span>
                  </button>

                  <div className="tab-actions">
                    <button
                      className={`btn-active-toggle ${
                        tab.isActive !== false ? 'active' : 'inactive'
                      }`}
                      onClick={() => handleToggleActive(tab)}
                      title={
                        tab.isActive !== false
                          ? 'Désactiver la page'
                          : 'Activer la page'
                      }
                    >
                      {tab.isActive !== false ? '👁️' : '👁️‍🗨️'}
                    </button>
                    <button
                      className="btn-edit"
                      onClick={() => handleStartEdit(tab)}
                      title="Modifier"
                    >
                      ✏️
                    </button>
                    {canDeleteTab(tab.id) && (
                      <button
                        className="btn-delete"
                        onClick={() => {
                          if (confirm(`Supprimer la page "${tab.label}" ?`)) {
                            deleteTab(tab.id);
                          }
                        }}
                        title="Supprimer"
                      >
                        🗑️
                      </button>
                    )}
                  </div>

                  <div className="tab-badges">
                    {tab.isDefault && (
                      <span className="default-badge" title="Page par défaut">
                        Défaut
                      </span>
                    )}
                    {tab.isActive === false && (
                      <span className="inactive-badge" title="Page inactive">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add New Tab */}
        {canAddTab() && (
          <div className="add-tab-section">
            <h4>Ajouter une nouvelle page</h4>
            <div className="add-tab-form">
              <input
                type="text"
                placeholder="Nom de la page"
                value={newTabLabel}
                onChange={(e) => setNewTabLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddTab();
                }}
                className="new-tab-input"
              />
              <button
                className="btn-add"
                onClick={handleAddTab}
                disabled={!newTabLabel.trim()}
              >
                + Ajouter
              </button>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        {/* <div className="quick-actions">
          <button
            className="btn-reset"
            onClick={() => {
              if (
                confirm(
                  'Réinitialiser aux pages par défaut ? Cela supprimera toutes vos pages personnalisées.'
                )
              ) {
                localStorage.removeItem('pageEditor_tabs');
                window.location.reload();
              }
            }}
          >
            🔄 Réinitialiser
          </button>
        </div> */}
      </div>
    </div>
  );
}
